import React, { useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Build as InstallationIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Timeline as TimelineIcon,
  Visibility,
  Edit,
  Close,
  Save,
  Add,
  Settings,
  TrendingUp,
  Power as PowerIcon,
  Home as HomeIcon,
  Business as BusinessIcon,
  Factory as FactoryIcon,
  Info as InfoIcon,
  AccessTime as TimeIcon,
} from '@mui/icons-material';

const InstallationType = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    status: '',
    category: '',
    searchTerm: '',
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [statusConfirmDialogOpen, setStatusConfirmDialogOpen] = useState(false);
  const [selectedInstallationType, setSelectedInstallationType] = useState(null);
  const [selectedForStatus, setSelectedForStatus] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [editFormData, setEditFormData] = useState({
    typeName: '',
    category: 'Residential',
    description: '',
    typicalDuration: '',
    complexityLevel: 'Medium',
    requiredTools: '',
    costRange: '',
    typicalCapacity: '',
    isActive: true,
    installationSteps: '',
    safetyRequirements: '',
  });

  const sampleData = [
    { 
      id: 1, 
      typeName: 'Residential Rooftop Solar', 
      category: 'Residential',
      description: 'Standard rooftop solar panel installation for residential properties',
      typicalDuration: '2-5 days',
      complexityLevel: 'Medium',
      requiredTools: 'Drill, Wrench Set, Ladder, Safety Equipment',
      costRange: '₹50,000 - ₹3,00,000',
      typicalCapacity: '1-10 kW',
      isActive: true,
      installationSteps: 'Site Survey, Roof Preparation, Panel Mounting, Wiring, Testing',
      safetyRequirements: 'Fall Protection, Electrical Safety, Weather Conditions',
      totalInstallations: 125,
      completedInstallations: 118,
      successRate: 94.4,
    },
    { 
      id: 2, 
      typeName: 'Ground Mount Solar System', 
      category: 'Commercial',
      description: 'Large-scale ground-mounted solar installation for commercial use',
      typicalDuration: '15-30 days',
      complexityLevel: 'High',
      requiredTools: 'Excavator, Crane, Heavy Equipment, Surveying Tools',
      costRange: '₹5,00,000 - ₹50,00,000',
      typicalCapacity: '10-500 kW',
      isActive: true,
      installationSteps: 'Land Preparation, Foundation, Mounting Structure, Panel Installation, Grid Connection',
      safetyRequirements: 'Heavy Equipment Operation, Electrical Safety, Site Security',
      totalInstallations: 45,
      completedInstallations: 42,
      successRate: 93.3,
    },
    { 
      id: 3, 
      typeName: 'Battery Storage System', 
      category: 'Residential',
      description: 'Installation of battery storage systems for energy backup',
      typicalDuration: '1-2 days',
      complexityLevel: 'Medium',
      requiredTools: 'Battery Mounting Kit, DC Tools, Electrical Testing Equipment',
      costRange: '₹1,00,000 - ₹5,00,000',
      typicalCapacity: '5-20 kWh',
      isActive: true,
      installationSteps: 'Location Selection, Mounting, Wiring, Commissioning, Testing',
      safetyRequirements: 'Electrical Safety, Ventilation, Fire Safety',
      totalInstallations: 78,
      completedInstallations: 75,
      successRate: 96.2,
    },
    { 
      id: 4, 
      typeName: 'Wind Turbine Installation', 
      category: 'Industrial',
      description: 'Small to medium wind turbine installation for renewable energy',
      typicalDuration: '7-14 days',
      complexityLevel: 'High',
      requiredTools: 'Crane, Foundation Equipment, Specialized Tools',
      costRange: '₹10,00,000 - ₹1,00,00,000',
      typicalCapacity: '5-50 kW',
      isActive: true,
      installationSteps: 'Site Analysis, Foundation, Tower Erection, Turbine Installation, Grid Connection',
      safetyRequirements: 'Height Safety, Heavy Equipment, Electrical Safety',
      totalInstallations: 28,
      completedInstallations: 26,
      successRate: 92.9,
    },
    { 
      id: 5, 
      typeName: 'Solar Water Heater', 
      category: 'Residential',
      description: 'Solar thermal water heating system installation',
      typicalDuration: '1-2 days',
      complexityLevel: 'Low',
      requiredTools: 'Basic Plumbing Tools, Mounting Brackets, Safety Equipment',
      costRange: '₹25,000 - ₹1,50,000',
      typicalCapacity: '100-300 L',
      isActive: true,
      installationSteps: 'Roof Mounting, Plumbing Connection, Testing, Commissioning',
      safetyRequirements: 'Roof Safety, Plumbing Safety, Hot Water Safety',
      totalInstallations: 95,
      completedInstallations: 90,
      successRate: 94.7,
    },
    { 
      id: 6, 
      typeName: 'Grid-Tied Solar System', 
      category: 'Commercial',
      description: 'Grid-connected solar system with net metering',
      typicalDuration: '5-10 days',
      complexityLevel: 'Medium',
      requiredTools: 'Standard Solar Tools, Grid Connection Equipment, Testing Tools',
      costRange: '₹2,00,000 - ₹20,00,000',
      typicalCapacity: '20-200 kW',
      isActive: false,
      installationSteps: 'System Design, Panel Installation, Inverter Setup, Grid Connection, Meter Installation',
      safetyRequirements: 'Electrical Safety, Grid Compliance, Inspection Requirements',
      totalInstallations: 62,
      completedInstallations: 55,
      successRate: 88.7,
    },
  ];

  const [data, setData] = useState(sampleData);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Edit form handlers
  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = () => {
    // Determine if we're creating or updating
    if (selectedInstallationType) {
      handleUpdateInstallationType();
    } else {
      handleCreateInstallationType();
    }
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  // New Installation Type handler
  const handleNewInstallationType = () => {
    console.log('Creating new installation type...');
    // Reset form data for new installation type
    setEditFormData({
      typeName: '',
      category: 'Residential',
      description: '',
      typicalDuration: '',
      complexityLevel: 'Medium',
      requiredTools: '',
      costRange: '',
      typicalCapacity: '',
      isActive: true,
      installationSteps: '',
      safetyRequirements: '',
    });
    setSelectedInstallationType(null);
    setEditDialogOpen(true);
  };

  // Create new installation type
  const handleCreateInstallationType = () => {
    if (!editFormData.typeName || !editFormData.category) {
      alert('Please fill in required fields: Type Name and Category');
      return;
    }

    // Create new installation type object
    const newInstallationType = {
      id: Date.now(),
      typeName: editFormData.typeName,
      category: editFormData.category,
      description: editFormData.description,
      typicalDuration: editFormData.typicalDuration,
      complexityLevel: editFormData.complexityLevel,
      requiredTools: editFormData.requiredTools,
      costRange: editFormData.costRange,
      typicalCapacity: editFormData.typicalCapacity,
      isActive: editFormData.isActive,
      installationSteps: editFormData.installationSteps,
      safetyRequirements: editFormData.safetyRequirements,
      totalInstallations: 0,
      completedInstallations: 0,
      successRate: 0,
    };

    console.log('Creating new installation type:', newInstallationType);
    setData(prev => [newInstallationType, ...prev]);
    alert(`New installation type "${newInstallationType.typeName}" created successfully!`);
    
    // Close dialog and reset form
    setEditDialogOpen(false);
    setSelectedInstallationType(null);
    setEditFormData({
      typeName: '',
      category: 'Residential',
      description: '',
      typicalDuration: '',
      complexityLevel: 'Medium',
      requiredTools: '',
      costRange: '',
      typicalCapacity: '',
      isActive: true,
      installationSteps: '',
      safetyRequirements: '',
    });
  };

  // Update existing installation type
  const handleUpdateInstallationType = () => {
    if (!editFormData.typeName || !editFormData.category) {
      alert('Please fill in required fields: Type Name and Category');
      return;
    }

    const updatedInstallationType = {
      ...selectedInstallationType,
      typeName: editFormData.typeName,
      category: editFormData.category,
      description: editFormData.description,
      typicalDuration: editFormData.typicalDuration,
      complexityLevel: editFormData.complexityLevel,
      requiredTools: editFormData.requiredTools,
      costRange: editFormData.costRange,
      typicalCapacity: editFormData.typicalCapacity,
      isActive: editFormData.isActive,
      installationSteps: editFormData.installationSteps,
      safetyRequirements: editFormData.safetyRequirements,
    };

    console.log('Updating installation type:', updatedInstallationType);
    setData(prev => prev.map(i => i.id === updatedInstallationType.id ? updatedInstallationType : i));
    alert(`Installation type "${updatedInstallationType.typeName}" updated successfully!`);
    
    // Close dialog and reset form
    setEditDialogOpen(false);
    setSelectedInstallationType(null);
    setEditFormData({
      typeName: '',
      category: 'Residential',
      description: '',
      typicalDuration: '',
      complexityLevel: 'Medium',
      requiredTools: '',
      costRange: '',
      typicalCapacity: '',
      isActive: true,
      installationSteps: '',
      safetyRequirements: '',
    });
  };

  // Search handler - applies all filters
  const handleSearch = () => {
    setSearchLoading(true);
    setHasSearched(true);
    console.log('Search triggered with filters:', {
      searchTerm: searchTerm,
      status: filters.status,
      category: filters.category,
    });
    // Simulate API call delay
    setTimeout(() => {
      setFilters(prev => ({ ...prev, searchTerm: searchTerm }));
      setSearchLoading(false);
    }, 500);
  };

  // View and Edit action handlers
  const handleView = (installationType) => {
    setSelectedInstallationType(installationType);
    setViewDialogOpen(true);
  };

  const handleEdit = (installationType) => {
    setSelectedInstallationType(installationType);
    setEditFormData({
      typeName: installationType.typeName,
      category: installationType.category,
      description: installationType.description,
      typicalDuration: installationType.typicalDuration,
      complexityLevel: installationType.complexityLevel,
      requiredTools: installationType.requiredTools,
      costRange: installationType.costRange,
      typicalCapacity: installationType.typicalCapacity,
      isActive: installationType.isActive,
      installationSteps: installationType.installationSteps,
      safetyRequirements: installationType.safetyRequirements,
    });
    setEditDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedInstallationType(null);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedInstallationType(null);
    setEditFormData({
      typeName: '',
      category: 'Residential',
      description: '',
      typicalDuration: '',
      complexityLevel: 'Medium',
      requiredTools: '',
      costRange: '',
      typicalCapacity: '',
      isActive: true,
      installationSteps: '',
      safetyRequirements: '',
    });
  };

  // Optimized filter function with memoization - only works when search is performed
  const getFilteredData = useCallback(() => {
    // If no search has been performed, return all data
    if (!hasSearched) {
      return data;
    }

    let filteredData = [...data];

    // Apply search filter
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      filteredData = filteredData.filter(item =>
        item.typeName.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        item.category.toLowerCase().includes(searchLower) ||
        item.complexityLevel.toLowerCase().includes(searchLower)
      );
    }

    // Apply status filter
    if (filters.status) {
      filteredData = filteredData.filter(item => 
        filters.status === 'active' ? item.isActive : !item.isActive
      );
    }

    // Apply category filter
    if (filters.category) {
      filteredData = filteredData.filter(item => 
        item.category === filters.category
      );
    }

    return filteredData;
  }, [hasSearched, filters.searchTerm, filters.status, filters.category, data]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);
  const totalTypes = filteredData.length;
  const activeTypes = filteredData.filter(item => item.isActive).length;
  const totalInstallations = filteredData.reduce((sum, item) => sum + item.totalInstallations, 0);
  const averageSuccessRate = filteredData.length > 0 ? 
    (filteredData.reduce((sum, item) => sum + item.successRate, 0) / filteredData.length).toFixed(1) : 0;

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const handleStatusToggle = (item) => {
    setSelectedForStatus(item);
    setStatusConfirmDialogOpen(true);
  };

  const confirmStatusToggle = () => {
    if (!selectedForStatus) return;
    setData(prev => prev.map(i => i.id === selectedForStatus.id ? { ...i, isActive: !i.isActive } : i));
    setStatusConfirmDialogOpen(false);
    setSelectedForStatus(null);
  };

  const getComplexityColor = (level) => {
    switch (level) {
      case 'Low': return 'success';
      case 'Medium': return 'warning';
      case 'High': return 'error';
      default: return 'default';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Residential': return <HomeIcon />;
      case 'Commercial': return <BusinessIcon />;
      case 'Industrial': return <FactoryIcon />;
      default: return <InstallationIcon />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Residential': return '#536DFE';
      case 'Commercial': return '#ff9800';
      case 'Industrial': return '#2196f3';
      default: return '#9e9e9e';
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Installation Type Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage renewable energy installation types and procedures
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleNewInstallationType}
            sx={{ 
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' },
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 2,
            }}
          >
            New Installation Type
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <InstallationIcon sx={{ fontSize: 28, color: '#536DFE', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {totalTypes}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Types
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <CheckCircleIcon sx={{ fontSize: 28, color: '#4caf50', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {activeTypes}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Active Types
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <PowerIcon sx={{ fontSize: 28, color: '#ff9800', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {totalInstallations}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Total Installations
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
              <TrendingUp sx={{ fontSize: 28, color: '#2196f3', mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: '#000' }}>
                {averageSuccessRate}%
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Avg Success Rate
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2 }}>
            Search & Filters
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {/* Search Input */}
            <TextField
              size="small"
              placeholder="Search installation type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              variant="outlined"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                      edge="end"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
              sx={{
                minWidth: 300,
                maxWidth: 400,
                flex: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            
            {/* Filter Controls */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Category</InputLabel>
              <Select 
                label="Category" 
                value={filters.category}
                onChange={(e) => {
                  setFilters({ ...filters, category: e.target.value });
                }}
                sx={{
                  borderRadius: 2,
                }}
              >
                <MenuItem value="">All Categories</MenuItem>
                <MenuItem value="Residential">Residential</MenuItem>
                <MenuItem value="Commercial">Commercial</MenuItem>
                <MenuItem value="Industrial">Industrial</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select 
                label="Status" 
                value={filters.status}
                onChange={(e) => {
                  setFilters({ ...filters, status: e.target.value });
                }}
                sx={{
                  borderRadius: 2,
                }}
              >
                <MenuItem value="">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="contained"
              size="small"
              onClick={handleSearch}
              disabled={searchLoading}
              startIcon={searchLoading ? <CircularProgress size={16} color="inherit" /> : <SearchIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                backgroundColor: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  backgroundColor: '#3F51B5',
                },
                '&:disabled': {
                  backgroundColor: '#e0e0e0',
                  color: '#9e9e9e',
                }
              }}
            >
              {searchLoading ? 'Searching...' : 'Search'}
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setFilters({
                  status: '',
                  category: '',
                  searchTerm: '',
                });
                setSearchTerm('');
                setHasSearched(false);
                setSearchLoading(false);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#536DFE',
                color: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#3F51B5',
                  backgroundColor: '#f5f5f5',
                }
              }}
            >
              Clear All
            </Button>
          </Box>
        </CardContent>
      </Card>

  {/* Status Toggle Confirmation Dialog */}
  <Dialog 
    open={statusConfirmDialogOpen} 
    onClose={() => setStatusConfirmDialogOpen(false)}
    maxWidth="sm"
    PaperProps={{
      sx: { borderRadius: 2 }
    }}
  >
    <DialogTitle sx={{
      backgroundColor: '#536DFE',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      py: 2,
      px: 3
    }}>
      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
        {selectedForStatus?.isActive ? 'Deactivate Installation Type' : 'Activate Installation Type'}
      </Typography>
    </DialogTitle>
    <DialogContent sx={{ p: 3, pt: 3 }}>
      <Typography variant="body1" sx={{ mb: 2 }}>
        Are you sure you want to {selectedForStatus?.isActive ? 'deactivate' : 'activate'} the installation type <strong>"{selectedForStatus?.typeName}"</strong>?
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {selectedForStatus?.isActive
          ? 'Deactivating this type will prevent it from being used in new workflows.'
          : 'Activating this type will make it available for use again.'
        }
      </Typography>
    </DialogContent>
    <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
      <Button 
        onClick={() => setStatusConfirmDialogOpen(false)}
        variant="outlined"
        sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}
      >
        Cancel
      </Button>
      <Button 
        onClick={confirmStatusToggle}
        variant="contained"
        color={selectedForStatus?.isActive ? 'warning' : 'success'}
        sx={{ textTransform: 'none', px: 3, py: 1, fontWeight: 600, borderRadius: 1.5 }}
      >
        {selectedForStatus?.isActive ? 'Deactivate' : 'Activate'}
      </Button>
    </DialogActions>
  </Dialog>

      {/* Search Results Summary */}
      {hasSearched && (
        <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2, border: '1px solid #e8f5e8' }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                p: 1,
                backgroundColor: '#e8f5e8',
                borderRadius: 2,
                minWidth: 'fit-content'
              }}>
                {searchLoading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <SearchIcon sx={{ color: '#536DFE' }} />
                )}
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#536DFE' }}>
                  Search Results
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                {searchLoading ? (
                  <Typography variant="body2" color="text.secondary">
                    Searching installation types...
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                      {filteredData.length} type{filteredData.length !== 1 ? 's' : ''} found
                    </Typography>
                    {filters.status && filters.status !== 'All Status' && (
                      <Chip
                        label={`Status: ${filters.status}`} 
                        size="small"
                        color="success" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                    {filters.category && filters.category !== 'All Categories' && (
                      <Chip
                        label={`Category: ${filters.category}`} 
                        size="small"
                        color="primary" 
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Installation Types
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Type Name</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Category</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Duration</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Complexity</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Capacity</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Installations</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Success Rate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {searchLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Searching installation types...
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => (
                    <TableRow 
                      key={row.id} 
                      hover
                      sx={{ 
                        '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                        '&:hover': { backgroundColor: '#e8f5e8 !important' }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ bgcolor: getCategoryColor(row.category), width: 32, height: 32 }}>
                            {getCategoryIcon(row.category)}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {row.typeName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {row.description.substring(0, 40)}...
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={row.category}
                          size="small" 
                          sx={{ 
                            backgroundColor: getCategoryColor(row.category),
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon sx={{ fontSize: 16, color: '#666' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.typicalDuration}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={row.complexityLevel}
                          size="small" 
                          color={getComplexityColor(row.complexityLevel)}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>{row.typicalCapacity}</TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.completedInstallations}/{row.totalInstallations}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Completed/Total
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={`${row.successRate}%`}
                          size="small" 
                          color={row.successRate >= 90 ? 'success' : row.successRate >= 80 ? 'warning' : 'error'}
                          sx={{ fontWeight: 600 }}
                        />
                      </TableCell>
                      <TableCell>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={row.isActive}
                              onChange={() => handleStatusToggle(row)}
                              color="success"
                              size="medium"
                            />
                          }
                          label={
                            <Chip
                              label={row.isActive ? 'Active' : 'Inactive'}
                              size="small"
                              color={getStatusColor(row.isActive)}
                              sx={{ fontWeight: 600 }}
                            />
                          }
                          sx={{ m: 0 }}
                        />
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small" 
                              onClick={() => handleView(row)}
                              sx={{ color: '#536DFE' }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Type">
                            <IconButton 
                              size="small" 
                              onClick={() => handleEdit(row)}
                              sx={{ color: '#ff9800' }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No installation types found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {filteredData.length > 0 && (
            <TablePagination
              
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog 
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <InstallationIcon />
            <Typography variant="h6" fontWeight="bold">
              Installation Type Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedInstallationType && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: getCategoryColor(selectedInstallationType.category), width: 64, height: 64 }}>
                  {getCategoryIcon(selectedInstallationType.category)}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedInstallationType.typeName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedInstallationType.description}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    <Chip 
                      label={selectedInstallationType.category}
                      size="small" 
                      sx={{ 
                        backgroundColor: getCategoryColor(selectedInstallationType.category),
                        color: 'white'
                      }}
                    />
                    <Chip 
                      label={selectedInstallationType.complexityLevel}
                      size="small" 
                      color={getComplexityColor(selectedInstallationType.complexityLevel)}
                    />
                    <Chip 
                      label={selectedInstallationType.isActive ? 'Active' : 'Inactive'} 
                      size="small" 
                      color={getStatusColor(selectedInstallationType.isActive)}
                    />
                  </Box>
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Installation Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoIcon color="primary" />
                        Installation Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Type Name:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedInstallationType.typeName}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Category:</Typography>
                          <Chip 
                            label={selectedInstallationType.category}
                            size="small" 
                            sx={{ 
                              backgroundColor: getCategoryColor(selectedInstallationType.category),
                              color: 'white'
                            }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Duration:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedInstallationType.typicalDuration}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Complexity:</Typography>
                          <Chip 
                            label={selectedInstallationType.complexityLevel}
                            size="small" 
                            color={getComplexityColor(selectedInstallationType.complexityLevel)}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Capacity Range:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedInstallationType.typicalCapacity}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Cost Range:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedInstallationType.costRange}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Requirements */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Settings color="primary" />
                        Requirements
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Required Tools:
                          </Typography>
                          <Typography variant="body2" fontWeight="600">{selectedInstallationType.requiredTools}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Installation Steps:
                          </Typography>
                          <Typography variant="body2" fontWeight="600">{selectedInstallationType.installationSteps}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                            Safety Requirements:
                          </Typography>
                          <Typography variant="body2" fontWeight="600">{selectedInstallationType.safetyRequirements}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Performance Metrics */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingUp color="primary" />
                    Performance Metrics
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="bold" color="primary">
                          {selectedInstallationType.totalInstallations}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Total Installations
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="bold" color="success.main">
                          {selectedInstallationType.completedInstallations}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Completed
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h4" fontWeight="bold" color="warning.main">
                          {selectedInstallationType.successRate}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Success Rate
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Installation Type Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: selectedInstallationType ? '#ff9800' : '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {selectedInstallationType ? <Edit /> : <Add />}
            <Typography variant="h6" fontWeight="bold">
              {selectedInstallationType ? 'Edit Installation Type' : 'New Installation Type'}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseEditDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 1 }}>
              {selectedInstallationType ? `Edit installation type: ${selectedInstallationType.typeName}` : 'Create new installation type'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Type Name"
                  required
                  value={editFormData.typeName}
                  onChange={(e) => handleEditFormChange('typeName', e.target.value)}
                  variant="outlined"
                  size="small"
                  error={!editFormData.typeName}
                  helperText={!editFormData.typeName ? 'Type name is required' : ''}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category</InputLabel>
                  <Select
                    value={editFormData.category}
                    onChange={(e) => handleEditFormChange('category', e.target.value)}
                    label="Category"
                  >
                    <MenuItem value="Residential">Residential</MenuItem>
                    <MenuItem value="Commercial">Commercial</MenuItem>
                    <MenuItem value="Industrial">Industrial</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Typical Duration"
                  value={editFormData.typicalDuration}
                  onChange={(e) => handleEditFormChange('typicalDuration', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., 2-5 days"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Complexity Level</InputLabel>
                  <Select
                    value={editFormData.complexityLevel}
                    onChange={(e) => handleEditFormChange('complexityLevel', e.target.value)}
                    label="Complexity Level"
                  >
                    <MenuItem value="Low">Low</MenuItem>
                    <MenuItem value="Medium">Medium</MenuItem>
                    <MenuItem value="High">High</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Typical Capacity"
                  value={editFormData.typicalCapacity}
                  onChange={(e) => handleEditFormChange('typicalCapacity', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., 1-10 kW"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Cost Range"
                  value={editFormData.costRange}
                  onChange={(e) => handleEditFormChange('costRange', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., ₹50,000 - ₹3,00,000"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Required Tools"
                  value={editFormData.requiredTools}
                  onChange={(e) => handleEditFormChange('requiredTools', e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="e.g., Drill, Wrench Set, Ladder"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Installation Steps"
                  value={editFormData.installationSteps}
                  onChange={(e) => handleEditFormChange('installationSteps', e.target.value)}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                  placeholder="e.g., Site Survey, Installation, Testing"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Safety Requirements"
                  value={editFormData.safetyRequirements}
                  onChange={(e) => handleEditFormChange('safetyRequirements', e.target.value)}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                  placeholder="e.g., Fall Protection, Electrical Safety"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={editFormData.description}
                  onChange={(e) => handleEditFormChange('description', e.target.value)}
                  variant="outlined"
                  size="small"
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={editFormData.isActive}
                      onChange={(e) => handleEditFormChange('isActive', e.target.checked)}
                      color="success"
                    />
                  }
                  label="Active Type"
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button
            onClick={handleCloseEditDialog}
            variant="outlined"
            sx={{ mr: 1 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEdit}
            variant="contained"
            startIcon={<Save />}
            sx={{ 
              backgroundColor: selectedInstallationType ? '#ff9800' : '#536DFE',
              '&:hover': { backgroundColor: selectedInstallationType ? '#f57c00' : '#3F51B5' }
            }}
          >
            {selectedInstallationType ? 'Save Changes' : 'Create Type'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstallationType;
